const AUTH_KEY = 'picture-auth';

export function getAuth() {
  try {
    return JSON.parse(localStorage.getItem(AUTH_KEY));
  } catch {
    return null;
  }
}

export function setAuth(auth) {
  if (auth) localStorage.setItem(AUTH_KEY, JSON.stringify(auth));
  else {
    localStorage.removeItem(AUTH_KEY);
    clearCache(); // đăng xuất thì không giữ dữ liệu admin trong trình duyệt
  }
  window.dispatchEvent(new Event('auth-change'));
}

// Bộ nhớ đệm dữ liệu đã tải (GET): mở lại trang thì hiện ngay bản lần trước, sau đó bản mới từ máy chủ thay vào.
// Lưu cả trong localStorage để tải lại trang (F5) vẫn có; đầy bộ nhớ hoặc bị chặn thì chỉ giữ trong phiên.
const CACHE_PREFIX = 'cache:';
const memoryCache = new Map();

function readCache(path) {
  if (memoryCache.has(path)) return memoryCache.get(path);
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + path);
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
}

function writeCache(path, data) {
  memoryCache.set(path, data);
  try {
    localStorage.setItem(CACHE_PREFIX + path, JSON.stringify(data));
  } catch {}
}

function clearCache() {
  memoryCache.clear();
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(CACHE_PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch {}
}

// Lời gọi có { cache: true } được lưu lại.
// peek(() => api.kaiAlbums()): lấy bản đã lưu của đúng lời gọi đó (không gọi máy chủ); chưa có thì undefined
let peeking = false;
export function peek(call) {
  peeking = true;
  try {
    return call();
  } finally {
    peeking = false;
  }
}

const ERROR_MESSAGES = {
  401: 'Phiên đăng nhập hết hạn, vui lòng đăng nhập lại',
  403: 'Bạn không có quyền admin',
  413: 'Ảnh quá lớn (tối đa 10MB)',
  503: 'Server chưa cấu hình Cloudinary nên chưa upload được ảnh',
};

// Lỗi do máy chủ chưa sẵn sàng (vd backend đang khởi động lại): mất mạng, 502/503/504
const isServerWaking = (status) => status === undefined || status === 502 || status === 503 || status === 504;

function request(path, options = {}) {
  if (peeking) return readCache(path);
  return send(path, options);
}

async function send(path, { method = 'GET', body, form, cache = false } = {}) {
  const headers = {};
  const auth = getAuth();
  if (auth) headers.Authorization = `Bearer ${auth.accessToken}`;
  if (body) headers['Content-Type'] = 'application/json';

  let res;
  try {
    res = await fetch('/api' + path, {
      method,
      headers,
      body: form || (body ? JSON.stringify(body) : undefined),
    });
  } catch {
    const error = new Error('Không kết nối được máy chủ');
    error.waking = true;
    throw error;
  }

  if (!res.ok) {
    if (res.status === 401 && auth) {
      setAuth(null);
      window.dispatchEvent(new Event('session-expired')); // trang đang mở tự báo "phiên đăng nhập đã hết"
    }
    const data = await res.json().catch(() => null);
    // Ưu tiên câu báo lỗi cụ thể từ backend (vd "Tài khoản x@gmail.com không có quyền quản trị")
    const error = new Error(data?.message || ERROR_MESSAGES[res.status] || `Lỗi ${res.status}`);
    error.status = res.status;
    error.waking = isServerWaking(res.status);
    throw error;
  }
  // Vừa sửa dữ liệu: bỏ các bản đã lưu để trang khác không hiện lại bản cũ (trang đang mở tự cập nhật)
  if (method !== 'GET') clearCache();
  if (res.status === 204) return null;
  const data = await res.json();
  if (cache) writeCache(path, data);
  return data;
}

// Tải dữ liệu, máy chủ đang khởi động thì tự thử lại (mỗi 4 giây, tối đa khoảng 1,5 phút).
// onWaiting() được gọi khi bắt đầu phải chờ, để trang hiện "Máy chủ đang khởi động…"
export async function loadWithRetry(load, onWaiting) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await load();
    } catch (error) {
      if (!error.waking || attempt >= 22) throw error;
      if (attempt === 0) onWaiting?.();
      await new Promise((resolve) => setTimeout(resolve, 4000));
    }
  }
}

export const api = {
  getAuthConfig: () => request('/auth/config'),
  loginGoogle: (credential) => request('/auth/google', { method: 'POST', body: { credential } }),

  // Tranh của một danh mục (key = id danh mục, hoặc 'none' = chưa phân loại); bản đầy đủ cho admin (có mã QR)
  getFolder: (key) => request(`/artworks/admin/all?${key === 'none' ? 'uncategorized=true' : `categoryId=${key}`}`, { cache: true }),
  searchArtworks: (q) => request(`/artworks/search?q=${encodeURIComponent(q)}`), // ô tìm nhanh, tối đa 8 kết quả
  getStats: () => request('/artworks/stats', { cache: true }), // { total, withQr, trash, uncategorized }
  getMaterials: () => request('/artworks/materials', { cache: true }), // chất liệu đã dùng (gợi ý trong form)
  getArtwork: (id) => request(`/artworks/${id}`, { cache: true }), // admin: bản đầy đủ (chủ sở hữu, mã QR)
  getArtworkByQr: (token) => request(`/artworks/qr/${encodeURIComponent(token)}`),
  ensureQr: (id) => request(`/artworks/${id}/qr`, { method: 'POST' }),

  create: (data) => request('/artworks', { method: 'POST', body: data }),
  update: (id, data) => request(`/artworks/${id}`, { method: 'PUT', body: data }),
  remove: (id) => request(`/artworks/${id}`, { method: 'DELETE' }),
  getTrash: () => request('/artworks/trash'),

  getCategories: () => request('/categories', { cache: true }),
  createCategory: (data) => request('/categories', { method: 'POST', body: data }),
  uploadCategoryImage: async (id, file) => {
    const form = new FormData();
    form.append('file', await shrinkImage(file));
    return request(`/categories/${id}/image`, { method: 'POST', form });
  },
  renameCategory: (id, data) => request(`/categories/${id}`, { method: 'PUT', body: data }),
  // target: id danh mục nhận tranh, null = không có danh mục, 'trash' = chuyển tranh vào thùng rác
  deleteCategory: (id, target) =>
    request(`/categories/${id}${target === 'trash' ? '?trash=true' : target ? `?targetId=${target}` : ''}`, { method: 'DELETE' }),
  restore: (id) => request(`/artworks/${id}/restore`, { method: 'POST' }),
  removePermanently: (id) => request(`/artworks/${id}/permanent`, { method: 'DELETE' }), // chỉ tranh chưa xuất QR

  // Trang /kai: album ảnh/video cho khách xem
  kaiAlbums: () => request('/kai/albums', { cache: true }),
  kaiAlbum: (id) => request(`/kai/albums/${id}`, { cache: true }),
  kaiAllMedia: () => request('/kai/media', { cache: true }), // mọi ảnh/video kèm tên bộ sưu tập (tìm kiếm, thống kê)
  kaiSettings: () => request('/kai/settings', { cache: true }), // { heroImageUrl }: ảnh nền phần đầu trang chủ
  setKaiHero: (data) => request('/kai/settings/hero', { method: 'PUT', body: data }),
  updateKaiSettings: (data) => request('/kai/settings', { method: 'PUT', body: data }), // { heroFit, heroPosition, taglines }
  clearKaiHero: () => request('/kai/settings/hero', { method: 'DELETE' }),
  createKaiAlbum: (name) => request('/kai/albums', { method: 'POST', body: { name } }),
  renameKaiAlbum: (id, name) => request(`/kai/albums/${id}`, { method: 'PUT', body: { name } }),
  deleteKaiAlbum: (id) => request(`/kai/albums/${id}`, { method: 'DELETE' }),
  kaiUploadSignature: () => request('/kai/upload-signature'),
  addKaiMedia: (albumId, data) => request(`/kai/albums/${albumId}/media`, { method: 'POST', body: data }),
  updateKaiMedia: (id, data) => request(`/kai/media/${id}`, { method: 'PUT', body: data }),
  deleteKaiMedia: (id) => request(`/kai/media/${id}`, { method: 'DELETE' }),

  getAnalytics: (period = 'month') => request(`/analytics/summary?period=${period}`),
};

// Thu nhỏ ảnh trong trình duyệt trước khi gửi (cạnh dài tối đa 2000px, JPEG) để ảnh chụp điện thoại vài MB
// còn khoảng 0,5–1MB, gửi qua Vercel không bị lỗi. Ảnh đã nhỏ hoặc không đọc được thì gửi nguyên file.
const MAX_IMAGE_PX = 2000;
async function shrinkImage(file) {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_IMAGE_PX / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size <= 1024 * 1024) return file;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff'; // ảnh PNG nền trong suốt -> nền trắng thay vì đen
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

// Tải file thẳng từ trình duyệt lên Cloudinary (không qua server) bằng chữ ký backend cấp; onProgress(0..1)
export function uploadToCloudinary(file, signature, onProgress) {
  const form = new FormData();
  form.append('file', file);
  form.append('api_key', signature.apiKey);
  form.append('timestamp', signature.timestamp);
  form.append('folder', signature.folder);
  form.append('signature', signature.signature);
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `https://api.cloudinary.com/v1_1/${signature.cloudName}/auto/upload`);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded / e.total);
    xhr.onload = () => {
      const data = JSON.parse(xhr.responseText || '{}');
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else reject(new Error(data.error?.message || `Tải lên lỗi ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('Mất kết nối khi tải lên'));
    xhr.send(form);
  });
}

// "50 x 70" -> "50 x 70 cm"; giữ nguyên nếu đã có đơn vị
export function formatSize(size) {
  if (!size) return '';
  return /^[\d\s.,x×*]+$/i.test(size) ? `${size.trim()} cm` : size;
}

// 7, 50 -> "07 / 50"; 7, null -> "07"; không có bản thứ -> ""
export function formatEdition(artwork) {
  if (!artwork.editionNo) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return artwork.editionTotal ? `${pad(artwork.editionNo)} / ${pad(artwork.editionTotal)}` : pad(artwork.editionNo);
}

// Link ảnh Cloudinary đã thu nhỏ theo chiều rộng (px), tự nén và chọn định dạng nhẹ (WebP/AVIF).
// Truyền width gấp đôi chỗ hiển thị để vẫn nét trên màn hình điện thoại.
export function imageUrl(url, width) {
  if (!url || !url.includes('/image/upload/')) return url;
  return url.replace('/image/upload/', `/image/upload/f_auto,q_auto,c_limit,w_${width}/`);
}

export function mainImage(artwork) {
  const images = artwork.images || [];
  return images.find((img) => img.isMain) || images[0];
}
